import { useState } from "react";
import dayjs from "dayjs";

import {
  Grid,
  Button,
  Typography,
  List,
  Box,
  Avatar,
  ThemeProvider,
  Badge,
  Tooltip,
  IconButton
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningIcon from "@mui/icons-material/Warning";

import { mergeDarkTheme } from "@aclymatepackages/themes";
import { LoadingButton } from "@aclymatepackages/atoms";
import { CircularProgress } from "@mui/material";

import Card from "../../atoms/mui/Card";
import ListItem from "../../atoms/mui/ListItem";
import SlideLayout from "../../layouts/SlideLayout";
import WarningPopup from "../../atoms/notifications/WarningPopup";
import TagAvatar from "../../atoms/icons/TagAvatar";
import EmployeesInput from "../../inputs/tags/EmployeesInput";
import OfficeInput from "../../inputs/tags/OfficeInput";
import VendorsInput from "../../inputs/tags/VendorsInput";

import { usePlaid } from "../../../helpers/integrations/plaid";
import { useCachedFirebaseCrud } from "../../../helpers/firebase";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import { useSubscriptionType } from "../../../helpers/hooks/companyData";
import usePermissions from "../../../helpers/hooks/usePermissions";

const AccountRow = ({ account, onUpdateAccount, taggedAccounts }) => {
  const { account_id: accountId, name, mask } = account;

  const defaultAccountTag = {
    accountId,
    tagType: null,
    tagId: null,
    tagName: null,
    isIgnored: false
  };

  const [accountTag, setAccountTag] = useState(
    taggedAccounts.find((tag) => tag.accountId === accountId) ||
      defaultAccountTag
  );
  const { tagType, tagId, tagName, isIgnored } = accountTag;

  const [activePopper, setActivePopper] = useState("");
  const [popperAnchorEl, setPopperAnchorEl] = useState(null);

  const formatAccountMask = (mask) => {
    if (!mask) {
      return "N/A";
    }
    return `********${mask}`;
  };

  const updateAccountTag = (tagType, tagId, tagName) => {
    const newTag = {
      accountId,
      tagType,
      tagId,
      tagName,
      isIgnored: false
    };
    setAccountTag(newTag);

    if (onUpdateAccount) {
      onUpdateAccount(accountId, newTag);
    }
  };

  const clearAccountTag = () => {
    const clearedTag = {
      accountId,
      tagType: null,
      tagId: null,
      tagName: null,
      isIgnored: false
    };
    setAccountTag(clearedTag);

    if (onUpdateAccount) {
      onUpdateAccount(accountId, clearedTag);
    }
  };

  const handleIgnoreAccount = () => {
    const ignoredTag = {
      accountId,
      tagType: null,
      tagId: null,
      tagName: null,
      isIgnored: !isIgnored
    };
    setAccountTag(ignoredTag);

    if (onUpdateAccount) {
      onUpdateAccount(accountId, ignoredTag);
    }
  };

  const handleEmployeeSelect = (employees = []) => {
    const [employee] = employees;
    if (!employee?.id) {
      return clearAccountTag();
    }

    updateAccountTag("employee", employee.id, employee.name);
  };

  const handleOfficeSelect = (office = {}) => {
    if (!office?.id) {
      return clearAccountTag();
    }

    updateAccountTag("office", office.id, office.name);
  };

  const handleVendorSelect = (vendor = {}) => {
    if (!vendor?.id) {
      return clearAccountTag();
    }

    updateAccountTag("vendor", vendor.id, vendor.name);
  };

  const propsObj = {
    activePopper,
    setActivePopper,
    popperAnchorEl,
    setPopperAnchorEl,
    totalWidth: 500,
    transactionSubcategory: "other"
  };

  const handleTagClick = (tagType) => (event) => {
    if (activePopper && activePopper === tagType) {
      setActivePopper("");
      setPopperAnchorEl(null);
    } else {
      setPopperAnchorEl(event.currentTarget);
      setActivePopper(tagType);
    }
  };

  const getCurrentEmployeeData = () => {
    if (tagType === "employee" && tagId) {
      return [{ id: tagId, name: tagName }];
    }
    return [];
  };

  const getCurrentOfficeData = () => {
    if (tagType === "office" && tagId) {
      return { id: tagId, name: tagName };
    }
    return null;
  };

  const getCurrentVendorData = () => {
    if (tagType === "vendor" && tagId) {
      return { id: tagId, name: tagName };
    }
    return null;
  };

  const AvatarBadge = ({ children, tagType: badgeTagType }) => {
    const isTagged = tagType === badgeTagType && tagName;

    if (!isTagged) {
      return children;
    }

    return (
      <Tooltip title={tagName}>
        <Badge color="error" overlap="circular" badgeContent="">
          {children}
        </Badge>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        mb: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        backgroundColor: "background.paper",
        position: "relative"
      }}
    >
      <Box>
        <Typography
          variant="body1"
          fontWeight="medium"
          sx={{ opacity: isIgnored ? 0.5 : 1 }}
        >
          {name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ opacity: isIgnored ? 0.5 : 1 }}
        >
          {formatAccountMask(mask)}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Box
          onClick={isIgnored ? undefined : handleTagClick("employees")}
          sx={{
            cursor: isIgnored ? "default" : "pointer",
            opacity: isIgnored ? 0.5 : 1
          }}
        >
          <AvatarBadge tagType="employee">
            <TagAvatar tag="employees" />
          </AvatarBadge>
        </Box>

        <Box
          onClick={isIgnored ? undefined : handleTagClick("offices")}
          sx={{
            cursor: isIgnored ? "default" : "pointer",
            opacity: isIgnored ? 0.5 : 1
          }}
        >
          <AvatarBadge tagType="office">
            <TagAvatar tag="offices" />
          </AvatarBadge>
        </Box>

        <Box
          onClick={isIgnored ? undefined : handleTagClick("vendors")}
          sx={{
            cursor: isIgnored ? "default" : "pointer",
            opacity: isIgnored ? 0.5 : 1
          }}
        >
          <AvatarBadge tagType="vendor">
            <TagAvatar tag="vendors" />
          </AvatarBadge>
        </Box>

        <IconButton onClick={handleIgnoreAccount} size="small">
          <Tooltip
            title={
              isIgnored
                ? "Process transactions from this account"
                : "Ignore this account when processing transactions"
            }
          >
            {isIgnored ? (
              <RestartAltIcon color="primary" />
            ) : (
              <CloseIcon color="error" />
            )}
          </Tooltip>
        </IconButton>
      </Box>

      <ThemeProvider theme={mergeDarkTheme}>
        {activePopper === "employees" && (
          <EmployeesInput
            employees={getCurrentEmployeeData()}
            setEmployees={handleEmployeeSelect}
            removeEmployee={() => clearAccountTag()}
            title="Tag an employee for this account"
            position="center"
            inputLength={3}
            icon
            multiple={false}
            {...propsObj}
          />
        )}

        {activePopper === "offices" && (
          <OfficeInput
            office={getCurrentOfficeData()}
            setOffice={handleOfficeSelect}
            title="Tag an office for this account"
            position="center"
            inputLength={3}
            icon
            {...propsObj}
          />
        )}

        {activePopper === "vendors" && (
          <VendorsInput
            vendor={getCurrentVendorData()}
            setVendor={handleVendorSelect}
            title="Tag a vendor for this account"
            position="center"
            inputLength={3}
            icon
            {...propsObj}
          />
        )}
      </ThemeProvider>
    </Box>
  );
};

const PlaidCard = ({ gridProps = { sm: 12 } }) => {
  const { activateSnackbar } = usePlatformLayoutContext();
  const [{ status: subscriptionStatus }] = useSubscriptionType("saas");
  const { canEdit } = usePermissions();

  const [plaidError, setPlaidError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSlideOpen, setIsSlideOpen] = useState(false);
  const [showDisconnectWarning, setShowDisconnectWarning] = useState(false);

  const [accountTaggingData, setAccountTaggingData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    institutionLogo: selectedItemInstitutionLogo,
    institutionName: selectedItemInstitutionName,
    itemId: selectedItemId,
    id: integrationId,
    taggedAccounts: originalTaggedAccounts = [],
    lastSyncAt: selectedItemLastSyncAt
  } = selectedItem || {};

  const {
    activeIntegrations,
    plaidIntegrationsLoading,
    accountsLoading,
    isLoading,
    launchPlaidLink,
    launchReauthentication,
    revokePlaidAccess,
    getAccountsForItem,
    refreshTransactions
  } = usePlaid({ setPlaidError });

  const { updateCollectionDoc } = useCachedFirebaseCrud();

  const hasIntegrationError = (integration) => {
    return (
      integration.status === "error" &&
      integration.errorCode &&
      !integration.revokedAt &&
      !integration.archived
    );
  };

  const getErrorTooltipText = (integration) => {
    if (!hasIntegrationError(integration)) {
      return "";
    }

    const errorMessages = {
      reauthentication: "Authentication error - Click for more information",
      sync: "Sync error - Click for more information"
    };

    if (integration.doesRequireReauthentication) {
      return errorMessages.reauthentication;
    }

    return errorMessages.sync;
  };

  const handleReauthentication = async () => {
    if (!selectedItem) {
      return;
    }

    try {
      await launchReauthentication(selectedItem.itemId);
    } catch (error) {
      console.error("Error during reauthentication:", error);
      setPlaidError("Failed to start reauthentication. Please try again.");
    }
  };

  const handleRefreshTransactions = async (integration) => {
    const { itemId, id: refreshIntegrationId } = integration;

    setIsRefreshing(true);

    try {
      await refreshTransactions(itemId, refreshIntegrationId);
      activateSnackbar({
        message: `Successfully initiated transaction refresh for ${integration.institutionName}`,
        alert: "success"
      });
    } catch (error) {
      console.error("Error refreshing transactions:", error);
      activateSnackbar({
        message: "Failed to refresh transactions. Please try again.",
        alert: "error"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const revokeAccess = async () => {
    if (!selectedItem) {
      return;
    }

    await revokePlaidAccess(integrationId);

    return setIsSlideOpen(false);
  };

  const formatLastSyncTime = () => {
    if (!selectedItemLastSyncAt) {
      return "Never";
    }

    const lastSyncDate = selectedItemLastSyncAt.toDate
      ? selectedItemLastSyncAt.toDate()
      : new Date(selectedItemLastSyncAt);

    return dayjs(lastSyncDate).format("MMMM D, YYYY [at] h:mm A");
  };

  const plaidSubtitle = () => {
    const subtitleMap = {
      loading:
        "Connecting to your bank account. Please follow the prompts in the new window.",
      noIntegrations:
        "Connect your bank accounts to automatically import transactions for faster accounting setup.",
      loadingAccounts: "Loading your connected bank accounts..."
    };

    if (isLoading) {
      return subtitleMap.loading;
    }
    if (!activeIntegrations.length) {
      return subtitleMap.noIntegrations;
    }
    if (accountsLoading) {
      return subtitleMap.loadingAccounts;
    }

    const integrationsWithErrors =
      activeIntegrations.filter(hasIntegrationError).length;
    const baseText = `Connected ${activeIntegrations.length} bank institution${
      activeIntegrations.length > 1 ? "s" : ""
    }`;

    if (integrationsWithErrors > 0) {
      return `${baseText}. ${integrationsWithErrors} integration${
        integrationsWithErrors > 1 ? "s have" : " has"
      } errors that need attention.`;
    }

    return `${baseText}.`;
  };

  const convertAccountTaggingDataToArray = () => {
    const allAccounts = getAccountsForItem(selectedItemId);

    return allAccounts.map((account) => {
      const currentTag = accountTaggingData[account.account_id];

      if (!currentTag || (!currentTag.tagType && !currentTag.isIgnored)) {
        return {
          accountId: account.account_id,
          tagType: null,
          tagId: null,
          tagName: null,
          isIgnored: false
        };
      }

      return currentTag;
    });
  };

  const hasChanges = () => {
    const currentTaggedAccounts = convertAccountTaggingDataToArray();

    return currentTaggedAccounts.some((currentTag) => {
      const originalTag = originalTaggedAccounts.find(
        (tag) => tag.accountId === currentTag.accountId
      );

      if (!originalTag) {
        const { tagType, tagId, tagName } = currentTag;
        return tagType || tagId || tagName;
      }

      const fieldsToCompare = ["accountId", "tagType", "tagId", "tagName"];
      const hasFieldChanges = fieldsToCompare.some(
        (field) => originalTag[field] !== currentTag[field]
      );

      const hasIgnoredChange =
        (originalTag.isIgnored || false) !== currentTag.isIgnored;

      return hasFieldChanges || hasIgnoredChange;
    });
  };

  const handleUpdateAccount = (accountId, accountTag) => {
    setAccountTaggingData((prev) => ({
      ...prev,
      [accountId]: accountTag
    }));
  };

  const handleSaveChanges = async () => {
    if (!integrationId || !hasChanges()) {
      return;
    }

    setIsSaving(true);

    try {
      const updatedTaggedAccounts = convertAccountTaggingDataToArray().filter(
        (tag) => (tag.tagType && tag.tagId && tag.tagName) || tag.isIgnored
      );

      await updateCollectionDoc(
        "integrations",
        integrationId,
        { taggedAccounts: updatedTaggedAccounts },
        "plaid-account-tagging"
      );

      setSelectedItem((prev) => ({
        ...prev,
        taggedAccounts: updatedTaggedAccounts
      }));

      console.log("Successfully saved account tagging changes");
    } catch (error) {
      console.error("Error saving account tagging changes:", error);
      setPlaidError(
        "Failed to save account tagging changes. Please try again."
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Card
        gridProps={gridProps}
        isLoading={plaidIntegrationsLoading}
        cardType="layout"
        title="Bank Accounts"
        subtitle={plaidSubtitle()}
        icon={<AccountBalanceIcon />}
        content={
          <Grid container direction="column" alignItems="center" spacing={2}>
            {!activeIntegrations.length && (
              <Grid item container justifyContent="center">
                <LoadingButton
                  isLoading={isLoading}
                  onClick={launchPlaidLink}
                  color="primary"
                  label="Connect Bank Account"
                  disabled={subscriptionStatus !== "active" || !canEdit}
                />
              </Grid>
            )}
            {plaidError && (
              <Grid item>
                <Typography variant="body2" align="center" color="error">
                  {plaidError}
                </Typography>
              </Grid>
            )}
            {activeIntegrations.length > 0 && (
              <Grid item style={{ width: "100%" }}>
                <List>
                  {activeIntegrations.map((integration) => {
                    const itemAccounts = getAccountsForItem(integration.itemId);
                    const accountText = itemAccounts.length !== 1 ? "s" : "";
                    const secondaryText = `${itemAccounts.length} account${accountText} connected`;

                    const getAvatarImg = () => {
                      if (integration.institutionLogoUrl) {
                        return integration.institutionLogoUrl;
                      }
                      if (integration.institutionLogo) {
                        return `data:image/png;base64,${integration.institutionLogo}`;
                      }
                      return undefined;
                    };

                    const getAvatar = () => {
                      if (
                        integration.institutionLogoUrl ||
                        integration.institutionLogo
                      ) {
                        return null;
                      }
                      return <AccountBalanceIcon />;
                    };

                    return (
                      <ListItem
                        key={integration.id}
                        primaryText={integration.institutionName}
                        secondaryText={secondaryText}
                        avatarImg={getAvatarImg()}
                        avatar={getAvatar()}
                        onButtonClick={() => {
                          setSelectedItem(integration);
                          return setIsSlideOpen(true);
                        }}
                        actions={[
                          ...(hasIntegrationError(integration)
                            ? [
                                {
                                  tooltip: getErrorTooltipText(integration),
                                  icon: <WarningIcon color="error" />,
                                  onClick: () => {
                                    setSelectedItem(integration);
                                    setIsSlideOpen(true);
                                  }
                                }
                              ]
                            : []),
                          {
                            tooltip: "Refresh transactions",
                            icon: isRefreshing ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RefreshIcon />
                            ),
                            onClick: () => {
                              handleRefreshTransactions(integration);
                            },
                            disabled: isRefreshing
                          }
                        ]}
                      />
                    );
                  })}
                </List>
              </Grid>
            )}
            {canEdit && activeIntegrations.length > 0 && (
              <Grid item container justifyContent="center">
                <LoadingButton
                  isLoading={isLoading}
                  onClick={launchPlaidLink}
                  color="primary"
                  label="Add Another Account"
                />
              </Grid>
            )}
          </Grid>
        }
      />

      {isSlideOpen && (
        <SlideLayout
          isSlideOpen
          setIsSlideOpen={setIsSlideOpen}
          header={
            <Grid container spacing={2} wrap="nowrap" alignItems="center">
              <Grid item>
                <Avatar
                  src={
                    selectedItemInstitutionLogo &&
                    `data:image/png;base64,${selectedItemInstitutionLogo}`
                  }
                  style={{
                    color: "white"
                  }}
                >
                  <AccountBalanceIcon />
                </Avatar>
              </Grid>
              <Grid item sm={10}>
                <Typography variant="h5">
                  {selectedItemInstitutionName}
                </Typography>
              </Grid>
            </Grid>
          }
          content={
            <Box sx={{ p: 2 }}>
              {/* Error warning popup */}
              {selectedItem && hasIntegrationError(selectedItem) && (
                <Box sx={{ mb: 2 }}>
                  <WarningPopup
                    warningTitle={
                      selectedItem.doesRequireReauthentication
                        ? "Authentication Required"
                        : "Sync Error"
                    }
                    warningInput={
                      <ThemeProvider theme={mergeDarkTheme}>
                        <Box sx={{ p: 2 }}>
                          {selectedItem.doesRequireReauthentication ? (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ mb: 2, color: "white" }}
                              >
                                Your bank connection needs to be
                                reauthenticated. This usually happens when:
                              </Typography>
                              <Box
                                component="ul"
                                sx={{
                                  marginTop: 1,
                                  marginBottom: 2,
                                  paddingLeft: 2
                                }}
                              >
                                <Typography
                                  component="li"
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  Your bank credentials have expired
                                </Typography>
                                <Typography
                                  component="li"
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  Your bank requires additional verification
                                </Typography>
                                <Typography
                                  component="li"
                                  variant="body2"
                                  sx={{ color: "white" }}
                                >
                                  There have been changes to your account
                                  security settings
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ mb: 2, color: "white" }}
                              >
                                Click "Reauthenticate" below to reconnect your
                                bank account.
                              </Typography>
                            </>
                          ) : (
                            <>
                              <Typography
                                variant="body2"
                                sx={{ mb: 2, color: "white" }}
                              >
                                An unexpected error occurred while syncing your
                                transactions. Please contact Aclymate support
                                for assistance.
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 1,
                                  fontWeight: "bold",
                                  color: "white"
                                }}
                              >
                                Error Details:
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, color: "white" }}
                              >
                                Error Code: {selectedItem.errorCode}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ mb: 1, color: "white" }}
                              >
                                Error Message: {selectedItem.errorMessage}
                              </Typography>
                              {selectedItem.requestId && (
                                <Typography
                                  variant="body2"
                                  sx={{ mb: 2, color: "white" }}
                                >
                                  Request ID: {selectedItem.requestId}
                                </Typography>
                              )}
                            </>
                          )}

                          <Grid container spacing={2} justifyContent="center">
                            {selectedItem.doesRequireReauthentication ? (
                              <Grid item>
                                <LoadingButton
                                  isLoading={isLoading}
                                  onClick={handleReauthentication}
                                  color="primary"
                                  variant="contained"
                                  label="Reauthenticate"
                                />
                              </Grid>
                            ) : (
                              <Grid item>
                                <Button
                                  onClick={() => setIsSlideOpen(false)}
                                  color="primary"
                                  variant="contained"
                                >
                                  Close
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </ThemeProvider>
                    }
                  />
                </Box>
              )}

              {getAccountsForItem(selectedItemId).map((account) => (
                <AccountRow
                  key={account.account_id}
                  account={account}
                  onUpdateAccount={handleUpdateAccount}
                  taggedAccounts={originalTaggedAccounts}
                  hasChanges={hasChanges()}
                />
              ))}

              {canEdit && hasChanges() && (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                  <LoadingButton
                    isLoading={isSaving}
                    onClick={handleSaveChanges}
                    color="primary"
                    variant="contained"
                    label="Save Changes"
                  />
                </Box>
              )}
            </Box>
          }
          footer={
            <>
              {showDisconnectWarning && (
                <Box sx={{ pb: 2 }}>
                  <WarningPopup
                    warningTitle="Are you sure you want to disconnect this bank account? If you disconnect this account, your transactions will not be synced anymore."
                    warningInput={
                      <ThemeProvider theme={mergeDarkTheme}>
                        <Grid
                          item
                          container
                          spacing={2}
                          justifyContent="center"
                        >
                          <Grid item>
                            <LoadingButton
                              isLoading={isLoading}
                              onClick={revokeAccess}
                              color="primary"
                              variant="contained"
                              label="Disconnect"
                            />
                          </Grid>
                          <Grid item>
                            <Button
                              onClick={() => setShowDisconnectWarning(false)}
                            >
                              Cancel
                            </Button>
                          </Grid>
                        </Grid>
                      </ThemeProvider>
                    }
                  />
                </Box>
              )}
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Last synced: {formatLastSyncTime()}
                  </Typography>
                </Grid>
                {canEdit && (
                  <Grid item>
                    <Button
                      color="error"
                      variant="contained"
                      onClick={() => setShowDisconnectWarning(true)}
                      disabled={isLoading}
                    >
                      Disconnect
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>
          }
        />
      )}
    </>
  );
};

export default PlaidCard;
